// Cloudinary Configuration
// Replace these with your actual Cloudinary credentials from the Cloudinary Dashboard
const CLOUD_NAME = "dmthidq25";
const UPLOAD_PRESET = "riya_maga"; // Must be an 'Unsigned' preset for client-side uploads

/**
 * Slugifies a string for use in filenames and public IDs.
 */
const slugifyTitle = (text) => {
    if (!text) return 'vehicle';
    return text
        .toString()
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .substring(0, 60); // Cloudinary public_id limit
};

/**
 * Compresses and resizes an image client-side before uploading.
 * Converts to WebP format and limits dimensions to maxWidth.
 * @param {File} file - The original image file
 * @param {number} maxWidth - Maximum width in pixels (default: 1200)
 * @param {number} quality - WebP quality 0-1 (default: 0.82)
 * @returns {Promise<Blob>} - The compressed image blob in WebP format
 */
const compressImage = (file, maxWidth = 1200, quality = 0.82) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const reader = new FileReader();

        reader.onload = (e) => {
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Scale down if wider than maxWidth
                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                // Try WebP first, fall back to JPEG
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            canvas.toBlob(
                                (jpegBlob) => resolve(jpegBlob),
                                'image/jpeg',
                                quality
                            );
                        }
                    },
                    'image/webp',
                    quality
                );
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
};

/**
 * Applies Cloudinary URL transformations for auto quality and WebP delivery.
 * @param {string} url - The original Cloudinary URL
 * @returns {string} - The optimized URL
 */
const optimizeCloudinaryUrl = (url) => {
    if (!url || !url.includes('cloudinary.com')) return url;
    return url.replace('/upload/', '/upload/f_webp,q_auto/');
};

/**
 * Uploads an image file to Cloudinary with SEO optimization.
 * - Compresses client-side (resize + WebP conversion)
 * - Sets SEO-friendly filename and public_id from the ad title
 * - Stores alt text, caption and title as Cloudinary context metadata
 * - Returns an optimized WebP delivery URL
 *
 * @param {File} file - The image file to upload
 * @param {string} adTitle - The ad title to use for alt text, filename, and metadata
 * @param {number} imageIndex - Index of image in the set (for unique naming, default: 0)
 * @returns {Promise<string>} - The optimized URL of the uploaded image
 */
export const uploadToCloudinary = async (file, adTitle = 'vehicle', imageIndex = 0) => {
    if (!file) return null;

    const slug = slugifyTitle(adTitle);
    // e.g. "toyota-aqua-for-sale-colombo-1.webp"
    const seoFileName = imageIndex > 0 ? `${slug}-${imageIndex + 1}.webp` : `${slug}.webp`;
    const altText = imageIndex > 0
        ? `${adTitle} - Image ${imageIndex + 1}`
        : adTitle;

    try {
        // Step 1: Compress image client-side
        const compressedBlob = await compressImage(file);

        const formData = new FormData();
        // SEO-friendly file name
        formData.append("file", compressedBlob, seoFileName);
        formData.append("upload_preset", UPLOAD_PRESET);

        // Cloudinary context: stores alt text, caption and title as image metadata
        // Format: key=value|key=value
        const contextString = [
            `alt=${altText}`,
            `caption=${adTitle}`,
            `custom_title=${adTitle}`
        ].join('|');
        formData.append("context", contextString);

        // Step 2: Upload to Cloudinary
        const response = await fetch(
            `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
            {
                method: "POST",
                body: formData,
            }
        );

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error?.message || "Cloudinary upload failed");
        }

        const data = await response.json();

        // Step 3: Return optimized URL with WebP and auto quality
        return optimizeCloudinaryUrl(data.secure_url);
    } catch (error) {
        console.error("Cloudinary Upload Error:", error);
        throw error;
    }
};


